
import { useEffect, useState } from "react";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { DiscountFormValues } from "@/types/discount";
import { SchoolSelect } from "./components/SchoolSelect";
import { ProductSelect } from "./components/ProductSelect";
import { PlanSelect } from "./components/PlanSelect";
import { DiscountTypeSelect } from "./components/DiscountTypeSelect";
import { DiscountValueInput } from "./components/DiscountValueInput";
import { StatusSelect } from "./components/StatusSelect";

interface DiscountFormProps {
  initialValues: DiscountFormValues;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  onSuccess: () => void;
  mode: "create" | "edit";
  discountId?: string;
}

export const DiscountForm = ({
  initialValues,
  isSubmitting,
  setIsSubmitting,
  onSuccess,
  mode,
  discountId,
}: DiscountFormProps) => {
  const { toast } = useToast();
  const [selectedProductId, setSelectedProductId] = useState<string>("");

  const form = useForm<DiscountFormValues>({
    defaultValues: initialValues,
  });

  useEffect(() => {
    form.reset(initialValues);
  }, [initialValues, form]);

  const onSubmit = async (values: DiscountFormValues) => {
    try {
      setIsSubmitting(true);

      // Create an object with the base values
      const discountData = {
        school_id: values.school_id,
        discount_type: values.discount_type,
        discount_value: values.discount_value,
        status: values.status,
        created_by: (await supabase.auth.getUser()).data.user?.id,
      };

      // Add product_id or plan_id only if they have values
      if (values.product_id) {
        Object.assign(discountData, { product_id: values.product_id });
      }
      if (values.plan_id) {
        Object.assign(discountData, { plan_id: values.plan_id });
      }

      const { error } = mode === "create"
        ? await supabase.from("discounts").insert(discountData)
        : await supabase
            .from("discounts")
            .update(discountData)
            .eq("id", discountId);

      if (error) throw error;

      toast({
        title: `Discount ${mode === "create" ? "created" : "updated"} successfully`,
      });
      onSuccess();
    } catch (error) {
      console.error(`Error ${mode}ing discount:`, error);
      toast({
        title: "Error",
        description: `Failed to ${mode} discount`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <SchoolSelect form={form} />
        <ProductSelect form={form} onProductSelect={setSelectedProductId} />
        <PlanSelect form={form} selectedProductId={selectedProductId} />
        <DiscountTypeSelect form={form} />
        <DiscountValueInput form={form} />
        <StatusSelect form={form} />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : mode === "create" ? "Create" : "Update"}
        </Button>
      </form>
    </Form>
  );
};
